import React from "react";
import { useTranslation } from "react-i18next";
import { Identifiable, Resource, Role } from "@planzone/dto";
import {
  useAddEveryoneToProject,
  useAddPlanzoneResourcesToProject,
  useProjectResourcesRetrieval,
} from "@planzone/view-models";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { ResourceToProjectForm } from "./resourceToProjectForm";
import { Props } from "./types";
import styles from "./styles.scss";

function PlanzoneResourceToProject({ cancel, project }: Props) {
  const { t } = useTranslation();
  const { projectResources } = useProjectResourcesRetrieval(project);
  const [selectedResource, setSelectedResource] = React.useState<
    Identifiable<Resource>[]
  >([]);
  const [selectedRole, setSelectedRole] =
    React.useState<null | Identifiable<Role>>();
  // Everyone stands for the whole planzone: picked resources would be added by it anyway.
  const [isEveryoneSelected, setIsEveryoneSelected] = React.useState(false);
  const { addPlanzoneResourcesToProject, isRequestPending: isAddPending } =
    useAddPlanzoneResourcesToProject();
  const { addEveryoneToProject, isRequestPending: isEveryonePending } =
    useAddEveryoneToProject();
  const isRequestPending = isAddPending || isEveryonePending;

  return (
    <Modal
      header={t("addPlanzoneResourceToProject.title")}
      className={styles.resourceToProject}
      close={cancel}
    >
      <div className={styles.body}>
        <ResourceToProjectForm
          project={project}
          roles={selectedRole ? [selectedRole] : []}
          selectedRole={selectedRole || undefined}
          selectedResources={selectedResource}
          selectRole={setSelectedRole}
          isEveryoneSelected={isEveryoneSelected}
          selectEveryone={(isSelected) => {
            setIsEveryoneSelected(isSelected);
            isSelected && setSelectedResource([]);
          }}
          selectResources={(resources) => {
            resources.length > 0 && setIsEveryoneSelected(false);
            setSelectedResource(
              resources.filter(
                ({ uuid }) =>
                  !projectResources.find(
                    ({ resource }) => uuid === resource.uuid,
                  ),
              ),
            );
          }}
        />
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          !isRequestPending &&
            selectedRole &&
            (isEveryoneSelected
              ? addEveryoneToProject({ project, role: selectedRole })
              : addPlanzoneResourcesToProject({
                  project: project,
                  resources: [...selectedResource],
                  role: selectedRole,
                }));
        }}
        className={styles.footer}
      >
        <Button type="cancel" press={cancel}>
          {t("addPlanzoneResourceToProject.cancel")}
        </Button>
        <Button
          status={isRequestPending ? "pending" : "none"}
          isDisabled={!selectedResource || !selectedRole}
          type="submit"
        >
          {t("addPlanzoneResourceToProject.send")}
        </Button>
      </form>
    </Modal>
  );
}

export { PlanzoneResourceToProject };
